import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Layers } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PERMIT_FORM_TYPES } from "@/lib/form-categories";
import { getErrorMessage } from "@/lib/form-errors";
import { formatDateTimeShort } from "@/lib/format";
import type { FormStatusVariant } from "@/types/form";
import { trpc } from "@/utils/trpc";

const STATUS_FILTERS = [
  { value: "pending_review", label: "Awaiting review" },
  { value: "draft", label: "Drafts" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All statuses" },
] as const;

const ALL_SITES_VALUE = "all-sites";

type ManagedForm = {
  id: string;
  createdAt?: string;
  submittedAt?: string | null;
  status: string;
  createdBy?: string;
  data?: unknown;
};

type ManagedFormRow = {
  form: ManagedForm;
  template?: { name?: string | null } | null;
  site?: { name?: string | null } | null;
  submittedByName?: string | null;
  submittedByEmail?: string | null;
};

type SiteSelectProps = {
  selectedSiteId: string;
  onSiteChange: (value: string) => void;
  sites: { id: string; name: string }[];
};

type FiltersSectionProps = {
  selectedSiteId: string;
  sites: { id: string; name: string }[];
  statusFilter: (typeof STATUS_FILTERS)[number]["value"];
  searchTerm: string;
  onSiteChange: (value: string) => void;
  onStatusChange: (value: (typeof STATUS_FILTERS)[number]["value"]) => void;
  onSearchChange: (value: string) => void;
};

type StatusFilterProps = {
  statusFilter: (typeof STATUS_FILTERS)[number]["value"];
  onStatusChange: (value: (typeof STATUS_FILTERS)[number]["value"]) => void;
};

type SearchInputProps = {
  searchTerm: string;
  onSearchChange: (value: string) => void;
};

type SubmittedFormsTableProps = {
  isLoading: boolean;
  items: ManagedFormRow[];
  detailBasePath: string;
};

function getFormTitle(data: unknown, templateName?: string | null) {
  if (templateName) {
    return templateName;
  }

  if (typeof data === "object" && data !== null && "meta" in data) {
    const meta = data.meta;
    if (
      typeof meta === "object" &&
      meta !== null &&
      "title" in meta &&
      typeof meta.title === "string"
    ) {
      return meta.title;
    }
  }

  return "Untitled form";
}

function formatStatusLabel(status: string) {
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSubmittedAtLabel(form: ManagedForm) {
  const submittedAt = form.submittedAt ?? form.createdAt;
  if (!submittedAt) {
    return "Recently";
  }

  return formatDateTimeShort(new Date(submittedAt));
}

function getSubmittedByLabel(item: ManagedFormRow) {
  return (
    item.submittedByName ??
    item.submittedByEmail ??
    item.form.createdBy ??
    "Unknown user"
  );
}

function SiteSelect({ selectedSiteId, onSiteChange, sites }: SiteSelectProps) {
  return (
    <div className="space-y-2 min-w-0">
      <Label
        className="text-sm font-medium text-foreground"
        htmlFor="submitted-permits-site"
      >
        Site
      </Label>
      <Select value={selectedSiteId} onValueChange={onSiteChange}>
        <SelectTrigger id="submitted-permits-site" aria-label="Select site">
          <SelectValue placeholder="Select site" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SITES_VALUE}>All sites</SelectItem>
          {sites.map((site) => (
            <SelectItem key={site.id} value={site.id}>
              {site.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusFilter({ statusFilter, onStatusChange }: StatusFilterProps) {
  return (
    <div className="space-y-2 min-w-0">
      <Label
        htmlFor="submitted-permits-status"
        className="text-sm font-medium text-foreground"
      >
        Status
      </Label>
      <Select value={statusFilter} onValueChange={onStatusChange}>
        <SelectTrigger
          id="submitted-permits-status"
          aria-label="Filter by status"
        >
          <SelectValue placeholder="Filter status" />
        </SelectTrigger>
        <SelectContent>
          {STATUS_FILTERS.map((filter) => (
            <SelectItem key={filter.value} value={filter.value}>
              {filter.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SearchInput({ searchTerm, onSearchChange }: SearchInputProps) {
  return (
    <div className="space-y-2 min-w-0">
      <Label
        htmlFor="submitted-permits-search"
        className="text-sm font-medium text-foreground"
      >
        Search
      </Label>
      <Input
        id="submitted-permits-search"
        value={searchTerm}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search by title, creator, or form ID"
      />
    </div>
  );
}

function LoadingSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
      <div className="border-b px-4 py-4">
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="divide-y">
        {Array.from({ length: count }, (_, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: stable placeholder rows
            key={index}
            className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,0.5fr)] md:items-center"
          >
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-8 w-8 justify-self-end" />
          </div>
        ))}
      </div>
    </div>
  );
}

function NoFormsEmpty({
  title = "No submitted forms found",
  description = "Try switching site or status, or search by a different keyword.",
}: {
  title?: string;
  description?: string;
}) {
  return (
    <Empty className="border-dashed">
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function FiltersSection({
  selectedSiteId,
  sites,
  statusFilter,
  searchTerm,
  onSiteChange,
  onStatusChange,
  onSearchChange,
}: FiltersSectionProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Filters</CardTitle>
        <CardDescription>
          Narrow the list by site, review status, or search term.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.2fr)]">
          <SiteSelect
            selectedSiteId={selectedSiteId}
            onSiteChange={onSiteChange}
            sites={sites}
          />
          <StatusFilter
            statusFilter={statusFilter}
            onStatusChange={onStatusChange}
          />
          <SearchInput
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SubmittedFormsTable({
  isLoading,
  items,
  detailBasePath,
}: SubmittedFormsTableProps) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (items.length === 0) {
    return <NoFormsEmpty />;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-base">Submitted forms</CardTitle>
          <CardDescription>
            Showing {items.length} submitted form
            {items.length === 1 ? "" : "s"}.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-56">Form</TableHead>
              <TableHead className="min-w-40">Site</TableHead>
              <TableHead className="min-w-40">Submitted by</TableHead>
              <TableHead className="min-w-40">Submitted</TableHead>
              <TableHead className="min-w-36">Status</TableHead>
              <TableHead className="text-right">Open</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const { form, template, site } = item;
              const title = getFormTitle(form.data, template?.name);
              const status = form.status as FormStatusVariant;
              const submittedBy = getSubmittedByLabel(item);

              return (
                <TableRow key={form.id} className="align-top">
                  <TableCell className="min-w-56">
                    <div className="space-y-1">
                      <Link
                        to={`/${detailBasePath}/${form.id}`}
                        className="font-medium text-foreground transition-colors hover:text-primary hover:underline"
                      >
                        {title}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        ID {form.id}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="min-w-40 text-muted-foreground">
                    {site?.name ?? "Unknown site"}
                  </TableCell>
                  <TableCell className="min-w-40 text-muted-foreground">
                    {submittedBy}
                  </TableCell>
                  <TableCell className="min-w-40 text-muted-foreground">
                    {getSubmittedAtLabel(form)}
                  </TableCell>
                  <TableCell className="min-w-36">
                    <Badge variant={status}>
                      {formatStatusLabel(form.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      asChild
                      size="sm"
                      variant="ghost"
                      aria-label={`Open ${title}`}
                    >
                      <Link to={`/${detailBasePath}/${form.id}`}>
                        <ChevronRight />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

type ManagerFormsIndexProps = {
  formTypes?: readonly string[];
  pageTitle?: string;
  pageDescription?: string;
  detailBasePath?: string;
};

export default function ManagerFormsIndex({
  formTypes = PERMIT_FORM_TYPES,
  pageTitle = "Permits",
  pageDescription = "Review submitted work permit requests for your managed sites.",
  detailBasePath = "forms/permits",
}: ManagerFormsIndexProps = {}) {
  const managedSites = useQuery(trpc.getManagedSites.queryOptions());
  const navigate = useNavigate();
  const [selectedSiteId, setSelectedSiteId] = useState<string>(ALL_SITES_VALUE);
  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_FILTERS)[number]["value"]>("all");
  const [searchTerm, setSearchTerm] = useState("");

  const effectiveSelectedSiteId = useMemo(() => {
    if (selectedSiteId === ALL_SITES_VALUE) {
      return ALL_SITES_VALUE;
    }

    if (managedSites.data?.some((site) => site.id === selectedSiteId)) {
      return selectedSiteId;
    }

    return ALL_SITES_VALUE;
  }, [managedSites.data, selectedSiteId]);

  const formsQuery = useQuery({
    ...trpc.forms.listBySite.queryOptions({
      siteId:
        effectiveSelectedSiteId === ALL_SITES_VALUE
          ? undefined
          : effectiveSelectedSiteId,
      status:
        statusFilter === "all" || !statusFilter ? undefined : [statusFilter],
      formTypes: formTypes ? [...formTypes] : undefined,
    }),
    placeholderData: (previousData) => previousData,
  });

  const items = useMemo(() => {
    if (!formsQuery.data) return [];

    if (!searchTerm.trim()) {
      return formsQuery.data;
    }

    const term = searchTerm.trim().toLowerCase();
    return formsQuery.data.filter((item) => {
      const { form, template } = item;
      const title = getFormTitle(form?.data, template?.name);
      const submittedBy = getSubmittedByLabel(item).toLowerCase();

      return (
        title.toLowerCase().includes(term) ||
        submittedBy.includes(term) ||
        (form.createdBy ?? "").toLowerCase().includes(term) ||
        (form.status ?? "").toLowerCase().includes(term) ||
        (form.id ?? "").toLowerCase().includes(term)
      );
    });
  }, [formsQuery.data, searchTerm]);

  const hasSites = Boolean(managedSites.data && managedSites.data.length > 0);

  if (managedSites.isLoading) {
    return (
      <PageContainer>
        <LoadingSkeleton count={2} />
      </PageContainer>
    );
  }

  if (managedSites.isError) {
    return (
      <PageContainer>
        <FormErrorAlert
          message={getErrorMessage(managedSites.error, "Failed to load sites.")}
        />
      </PageContainer>
    );
  }

  if (!hasSites) {
    return (
      <PageContainer className="flex min-h-full flex-col gap-6 lg:gap-8">
        <PageHeader title={pageTitle} description={pageDescription} />
        <NoFormsEmpty
          title="No submitted forms yet"
          description="Forms will appear here once your first managed site starts receiving submissions."
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer className="flex min-h-full flex-col gap-6 lg:gap-8">
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        toolbar={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(
                effectiveSelectedSiteId !== ALL_SITES_VALUE
                  ? `/forms/templates?siteId=${effectiveSelectedSiteId}`
                  : "/forms/templates"
              )
            }
          >
            <Layers /> Browse templates
          </Button>
        }
      />

      <div className="space-y-6">
        <FiltersSection
          selectedSiteId={effectiveSelectedSiteId}
          sites={managedSites.data ?? []}
          statusFilter={statusFilter}
          searchTerm={searchTerm}
          onSiteChange={setSelectedSiteId}
          onStatusChange={(value) =>
            setStatusFilter(value as (typeof STATUS_FILTERS)[number]["value"])
          }
          onSearchChange={setSearchTerm}
        />

        {formsQuery.isError ? (
          <FormErrorAlert
            message={getErrorMessage(formsQuery.error, "Failed to load forms.")}
          />
        ) : (
          <SubmittedFormsTable
            isLoading={formsQuery.isLoading}
            items={items}
            detailBasePath={detailBasePath}
          />
        )}
      </div>
    </PageContainer>
  );
}
